import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
// import { environment } from "@environment";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, concatMap, filter, map, shareReplay, skip, take, tap, withLatestFrom } from "rxjs/operators";
import { EInterceptorOptionsHTTPHeaders } from "src/app/_helpers/interceptors/EInterceptorOptionsHTTPHeaders";
import { Utilities } from "src/app/_helpers/utility/utilities";
import { IAnalysis } from "src/app/_interfaces/IAnalysis";
import { AnalysisFacadeService } from "src/app/_services/analysis/analysis-facade.service";
import { ConfigService } from "src/app/_services/config.service";

@Injectable({
    providedIn: "root",
})
export class AnalysisNotesService {
    private analysisNoteTypes: AnalysisNoteType[] = AllAnalysisNoteTypes
    private _notes = new BehaviorSubject<AnalysisNote[]>([])
    public notes$ = this._notes.asObservable().pipe(skip(1))
    private analysis$ = this.afs.analysis$
    private _dropdownTypes = new BehaviorSubject<{type: AnalysisNoteType, disabled: boolean}[]>(null)
    public dropdownTypes$= this._dropdownTypes.asObservable()

    constructor(private http: HttpClient, private environment: ConfigService, private afs: AnalysisFacadeService) {
        this.analysis$ = this.afs.analysis$
        this.initiateStateLoop().subscribe() // Doesn't complete, so only call ONCE.
    }
    public refreshNotes(){
        const noErrHeader = Utilities.appendHTTPHeaderForInterceptor([EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor])
        return this.analysis$.pipe(
            take(1),
            concatMap<IAnalysis, Observable<AnalysisNote[]>>((analysis)=>{
                return  this.http.get<AnalysisNote[]>(`${this.environment.analysisServiceApi.baseUrl}/analysisNotes/analysis/${analysis.analysisId}`, {headers: noErrHeader})
            }),
            catchError((err)=>{
                return of([])
            }),
            tap<AnalysisNote[]>((notes)=> {
                this._notes.next(notes)
            })
        )
    }
    private initiateStateLoop(){
        const noErrHeader = Utilities.appendHTTPHeaderForInterceptor([EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor])        
        return  this.analysis$.pipe(
            filter((analysis)=>{
                if(analysis){
                    return true
                }
                return false
            }),
            withLatestFrom(this.afs.analysisRestricted$), 
            tap(([analysis, restricted]: [IAnalysis, boolean])=>{
                if(restricted){
                    const dd = this.analysisNoteTypes.map((analysisNoteType)=> {
                        if(analysisNoteType === "Request Description"){
                            return {disabled: true, type: analysisNoteType }
                        }
                        return {disabled: false, type: analysisNoteType }
                    })
                    this._dropdownTypes.next(dd)
                } else {
                    const dd = this.analysisNoteTypes.map((analysisNoteType)=> {
                        if(analysisNoteType === "Request Description"){
                            return {disabled: true, type: analysisNoteType }
                        }
                        return {disabled: false, type: analysisNoteType }
                    })
                    this._dropdownTypes.next(dd)
                }
            }),
            map(([analysis, restricted]: [IAnalysis, boolean])=>{ // drop the restricted argument for easier reading
                return analysis
            }),
            concatMap<IAnalysis, Observable<AnalysisNote[]>>((analysis)=>{
                return  this.http.get<AnalysisNote[]>(`${this.environment.analysisServiceApi.baseUrl}/analysisNotes/analysis/${analysis.analysisId}`, {headers: noErrHeader})
                .pipe(
                    catchError((err)=>{
                        return of([]) // 404's are expected and normal. So just pass an empty array to noteslist
                    }),
                    tap((notes: AnalysisNote[])=>{
                        this._notes.next(notes)
                    }),
                )
            }),
        )
    }
    saveNote( analysisNote: AnalysisNote) {
        return this.analysis$.pipe(
            take(1),
            concatMap((analysis: IAnalysis)=>{
                return this.http.put(`${this.environment.analysisServiceApi.baseUrl}/analysisNotes/analysis/${analysis.analysisId}`, analysisNote);
            })
        ) 
    }
    deleteNote(analysisNote: AnalysisNote) {
        return this.http.delete(`${this.environment.analysisServiceApi.baseUrl}/analysisNotes/${analysisNote.analysisNotesId}` )
    }
}


export type Note = {
    content: string;
    title: string;
    type: string;
    createdDate?: number;
    lastModifiedDate?: number;
    createdByUser?: string;
    lastModifiedByUser?: string;
}
export type CreateNoteFormValue = {
    content: string;
    title: string;
    type: string;
}
export class AnalysisNote implements Note  {
    analysisNotesId?: number;
    content: string;
    title: string;
    type: AnalysisNoteType;
    createdDate?: number;
    lastModifiedDate?: number;
    createdByUser?: string;
    lastModifiedByUser?: string;
};

export type AnalysisNoteType = "Weather" | "General Input" | "Landscape" | "Results" | "Status" | "Request Description";
export const AllAnalysisNoteTypes: AnalysisNoteType[] = ["Weather", "General Input", "Landscape", "Results", "Status", "Request Description"];
/**
 * @description "read only" note types, that can never be edited or destroyed after creation. This is for state management in the UI, and any future additions to this list should be validated and protected from modification on the backend as well. 
 * @constant Status "Status"
 * @constant Request Description "Request Description"*/
export const ImmutableAnalysisNoteTypes: AnalysisNoteType[] = AllAnalysisNoteTypes.filter((noteType)=>{
    if(noteType === "Status" || noteType === "Request Description"){
        return true
    }
    return false
})